import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import InvoiceTable from '@accounting/components/tables/invoice-table/invoice-table.vue';
import OrderTable from '@accounting/components/tables/order-table/order-table.vue';

@Component({
  name: 'bpc-invoice-container',
  components: {
    'bpc-invoice-table': InvoiceTable,
    'bpc-order-table': OrderTable,
  },
})
export default class InvoiceContainer extends Vue {
  $refs: {
    orderTable: InstanceType<typeof OrderTable>;
    invoiceTable: InstanceType<typeof InvoiceTable>;
  };

  // Selects the table to show 'orders' || 'invoices'
  public tableToShow = null;
  //Filter used in the tableToShow
  public filterConfigName: string = '';
  // Extended filters only used in orders at the moment
  public extendedFilterConfigName: string = '';
  // Filter fields to exclude of the table
  public filtersExcluded: string[] = [];
  // Title of the table to show
  public tableTitle = 'global.models.invoice.heading';
  // Subtitle of the table to show
  public tableSubtitle = 'global.models.invoice.subheading';
  // Set the type of accounting journals that shows the generate invoice Form and validate form.
  public accountingJournalsType: string[] = ['invoice'];
  // Enable/disable page actions. (Disabled by default)
  public validateInvoicePageAction: boolean = null;
  public sendInvoiceAction: boolean = null;
  public syncInvoicePageAction: boolean = null;
  public syncPaymentPageAction: boolean = null;
  public bulkMoveToTrashPageAction: boolean = true;
  public markInvoicesAsPaidPageAction: boolean = true;
  // Create invoice form config name
  public formConfigName: string = 'create-invoice-form';
  public displayConfigName: string = 'table';

  // LIFECYCLE
  mounted() {
    this.onChangeRoute();
  }

  // WATCHERS
  @Watch('$route.name')
  onChangeRoute() {
    switch (this.$route.name) {
      case 'admin-orders-to-invoice':
        this.tableToShow = 'orders';
        this.filterConfigName = 'orders-to-invoice';
        this.extendedFilterConfigName = 'orders-to-invoice-extended';
        this.filtersExcluded = ['status', 'hasNoInvoice', 'onlyPositivePrice', 'needToBeInvoiced'];
        this.tableTitle = 'accounting.invoices.tabs.orders_to_invoice.title';
        this.tableSubtitle = 'accounting.invoices.tabs.orders_to_invoice.subtitle';
        break;
      case 'admin-invoices-in-draft':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'draft-invoices';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.invoices.tabs.invoices_in_draft.title';
        this.tableSubtitle = 'accounting.invoices.tabs.invoices_in_draft.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = true;
        this.sendInvoiceAction = false;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = true;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'invoices-draft';
        break;
      case 'admin-invoices-to-pay':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'to-pay-invoices';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.invoices.tabs.invoices_in_to_pay.title';
        this.tableSubtitle = 'accounting.invoices.tabs.invoices_in_to_pay.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = true;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = true;
        this.displayConfigName = 'table';
        break;
      case 'admin-invoices-paid-and-to-pay':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'paid-and-to-pay-invoices';
        this.filtersExcluded = ['type'];
        this.tableTitle = 'accounting.invoices.tabs.invoices_paid_and_to_pay.title';
        this.tableSubtitle = '';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = true;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = true;
        this.displayConfigName = 'table';
        break;
      case 'admin-invoices-paid':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'paid-invoices';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.invoices.tabs.invoices_in_paid.title';
        this.tableSubtitle = 'accounting.invoices.tabs.invoices_in_paid.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'table';
        break;
      case 'admin-invoices-to-sync':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'to-sync-invoices';
        this.filtersExcluded = ['status', 'type', 'has_invoice_sync_item'];

        this.tableTitle = 'accounting.invoices.tabs.invoices_to_sync.title';
        this.tableSubtitle = 'accounting.invoices.tabs.invoices_to_sync.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = false;
        this.syncInvoicePageAction = true;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'invoices-to-sync';
        break;
      case 'admin-invoices-synchronized':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'synchronized-invoices';
        this.filtersExcluded = ['status', 'type', 'has_invoice_sync_item'];
        this.tableTitle = 'accounting.invoices.tabs.invoices_synchronized.title';
        this.tableSubtitle = 'accounting.invoices.tabs.invoices_synchronized.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = true;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = true;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'invoices-synced';
        break;
      case 'admin-invoices-trashed':
        this.tableToShow = 'invoices';
        this.filterConfigName = 'trashed-invoices';
        this.filtersExcluded = ['status', 'type'];
        this.tableTitle = 'accounting.invoices.tabs.invoices_trashed.title';
        this.tableSubtitle = 'accounting.invoices.tabs.invoices_trashed.subtitle';
        this.extendedFilterConfigName = '';
        this.validateInvoicePageAction = false;
        this.sendInvoiceAction = false;
        this.syncInvoicePageAction = false;
        this.syncPaymentPageAction = false;
        this.bulkMoveToTrashPageAction = false;
        this.markInvoicesAsPaidPageAction = false;
        this.displayConfigName = 'invoices-trashed';
        break;
      default:
        break;
    }
  }
}
